import { request } from "../../../services/api"
import type { BaseApiResponse } from "../../../types/api.ts"
import type {
  Channel,
  ComposeOutcome,
  ComposePayload,
  ContactRef,
  PreviewPayload,
  PreviewResult,
  SendState
} from "../types/compose.types.ts"

const BASE_URL = "/communication"

export const getSendState = (contact: ContactRef) =>
  request.api.get<SendState, BaseApiResponse<void>>(
    `${BASE_URL}/contacts/${contact.type}/${contact.id}/send-state/`
  )

export const previewMessage = (payload: PreviewPayload) =>
  request.api.post<BaseApiResponse<PreviewResult>, BaseApiResponse<void>>(
    `${BASE_URL}/messages/preview/`,
    payload
  )

export const sendMessage = (payload: ComposePayload) =>
  request.api.post<BaseApiResponse<ComposeOutcome>, BaseApiResponse<void>>(
    `${BASE_URL}/messages/`,
    payload
  )

export interface RecordFile {
  ref: string
  type: "document" | "photo"
  id: string
  name: string
}

const listOf = <T>(data: { results: T[] } | T[] | undefined): T[] =>
  Array.isArray(data) ? data : (data?.results ?? [])

export const getRecordFiles = async (leadId: string): Promise<RecordFile[]> => {
  const [documents, photos] = await Promise.all([
    request.api.get<
      { results: Array<{ id: string; file_name: string }> },
      BaseApiResponse<void>
    >("/project-management/project-attachments/", {
      lead: leadId,
      page_size: 200
    }),
    request.api.get<
      Array<{ id: string; original_filename: string }>,
      BaseApiResponse<void>
    >("/project-management/attachments/", { project: leadId, page_size: 200 })
  ])
  return [
    ...(documents.ok ? listOf(documents.data) : []).map((file) => ({
      ref: `document:${file.id}`,
      type: "document" as const,
      id: file.id,
      name: file.file_name
    })),
    ...(photos.ok ? listOf(photos.data) : []).map((file) => ({
      ref: `photo:${file.id}`,
      type: "photo" as const,
      id: file.id,
      name: file.original_filename
    }))
  ]
}

export interface ContactSuggestion extends ContactRef {
  name: string
  address: string
}

export const searchContacts = (q: string, channel: Channel) =>
  request.api.get<{ results: ContactSuggestion[] }, BaseApiResponse<void>>(
    "/communication/contacts/search/",
    { q, channel }
  )
